import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, Volume2, VolumeX } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getYouTubeEmbedUrl, formatDate, resolveMediaUrl } from '@/utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const VideoTheater = ({ game, isOpen, onClose }) => {
  const [adState, setAdState] = useState('loading'); // 'loading', 'playing', 'skippable', 'completed'
  const [adData, setAdData] = useState(null);
  const [countdown, setCountdown] = useState(5); // Seconds until skip available
  const [adTimeRemaining, setAdTimeRemaining] = useState(30);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const recordImpression = useCallback(async (adId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ads/impression?ad_id=${adId}&placement=video_player`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to record impression:', error);
    }
  }, []);

  const startAdTimer = useCallback((ad) => {
    const duration = ad.duration_seconds || 30;
    setAdTimeRemaining(duration);
    setCountdown(5);
    
    timerRef.current = setInterval(() => {
      setAdTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setAdState('completed');
          return 0;
        }
        return prev - 1;
      });
      
      setCountdown(prev => {
        if (prev <= 1) {
          setAdState('skippable');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startSponsorTimer = useCallback(() => {
    setAdTimeRemaining(10); // 10 seconds for sponsor logo
    setCountdown(3);
    
    timerRef.current = setInterval(() => {
      setAdTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setAdState('completed');
          return 0;
        }
        return prev - 1;
      });
      
      setCountdown(prev => {
        if (prev <= 1) {
          setAdState('skippable');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Fetch pre-roll ad when dialog opens
  useEffect(() => {
    const fetchPreRollAd = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/ads/serve/video_player`);
        const data = await response.json();
        
        if (data.type === 'ad' && data.ad) {
          setAdData(data.ad);
          setAdState('playing');
          startAdTimer(data.ad);
          recordImpression(data.ad.id);
        } else if (data.type === 'sponsors' && data.sponsors?.length > 0) {
          const sponsor = data.sponsors[Math.floor(Math.random() * data.sponsors.length)];
          setAdData({
            id: sponsor.id,
            image_url: sponsor.logo_url,
            click_url: sponsor.click_url,
            company_name: sponsor.company_name,
            ad_type: 'sponsor'
          });
          setAdState('playing');
          startSponsorTimer();
        } else {
          setAdState('completed');
        }
      } catch (error) {
        console.error('Failed to fetch pre-roll ad:', error);
        setAdState('completed');
      }
    };

    if (isOpen && game) {
      fetchPreRollAd();
    } else {
      setAdState('loading');
      setAdData(null);
      setCountdown(5);
      setAdTimeRemaining(30);
      if (timerRef.current) clearInterval(timerRef.current);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, game, startAdTimer, startSponsorTimer, recordImpression]);

  const recordClick = async () => {
    if (adData?.id && adData.ad_type !== 'sponsor') {
      try {
        await fetch(`${BACKEND_URL}/api/ads/click?ad_id=${adData.id}`, {
          method: 'POST'
        });
      } catch (error) {
        console.error('Failed to record click:', error);
      }
    }
    if (adData?.click_url) {
      window.open(adData.click_url, '_blank');
    }
  };

  const handleSkipAd = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setAdState('completed');
  };

  const handleVideoEnded = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setAdState('completed');
  };

  if (!game) return null;

  const freeSource = game.video_sources?.find(vs => vs.is_free);
  const embedUrl = freeSource?.embed_url || (freeSource?.type === 'youtube' ? getYouTubeEmbedUrl(freeSource?.url) : freeSource?.url);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-gray-900 border-sky-900/50 p-0">
        <DialogHeader className="p-4 border-b border-sky-900/30">
          <DialogTitle className="text-white">
            {game.home_team_name} vs {game.away_team_name}
          </DialogTitle>
          <div className="text-sm text-gray-400">
            {game.sport_name} / {game.league_name} - {formatDate(game.scheduled_time)}
          </div>
        </DialogHeader>
        
        <div className="aspect-video w-full bg-black relative">
          {/* Pre-roll Ad */}
          {adState !== 'completed' && adData && (
            <div className="absolute inset-0 z-20 bg-black flex items-center justify-center">
              {/* Video Ad */}
              {adData.video_url ? (
                <video
                  ref={videoRef}
                  src={resolveMediaUrl(adData.video_url)}
                  className="w-full h-full object-contain"
                  autoPlay
                  muted={muted}
                  onEnded={handleVideoEnded}
                  onClick={recordClick}
                  style={{ cursor: adData.click_url ? 'pointer' : 'default' }}
                />
              ) : (
                /* Image/Logo Ad */
                <div 
                  className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 cursor-pointer"
                  onClick={recordClick}
                >
                  <img 
                    src={resolveMediaUrl(adData.image_url)} 
                    alt="Advertisement"
                    className="max-w-md max-h-64 object-contain"
                  />
                  {adData.company_name && (
                    <p className="text-white text-lg mt-4 font-semibold">
                      Sponsored by {adData.company_name}
                    </p>
                  )}
                  {adData.click_url && (
                    <p className="text-sky-400 text-sm mt-2">Click to learn more</p>
                  )}
                </div>
              )}
              
              {/* Ad Overlay UI */}
              <div className="absolute top-4 left-4 bg-black/70 px-3 py-1.5 rounded text-white text-sm font-medium">
                Ad • {adTimeRemaining}s
              </div>
              
              {/* Skip Button */}
              <div className="absolute bottom-4 right-4">
                {adState === 'playing' && countdown > 0 ? (
                  <div className="bg-black/70 px-4 py-2 rounded text-white text-sm">
                    Skip in {countdown}s
                  </div>
                ) : adState === 'skippable' ? (
                  <Button
                    onClick={handleSkipAd}
                    className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
                  >
                    Skip Ad →
                  </Button>
                ) : null}
              </div>
              
              {/* Mute Button for Video Ads */}
              {adData.video_url && (
                <button
                  onClick={() => setMuted(!muted)}
                  className="absolute bottom-4 left-4 bg-black/70 p-2 rounded text-white hover:bg-black/90"
                >
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              )}
            </div>
          )}
          
          {/* Loading State */}
          {adState === 'loading' && (
            <div className="absolute inset-0 z-20 bg-black flex items-center justify-center">
              <div className="text-white">Loading...</div>
            </div>
          )}
          
          {/* Main Video Content */}
          {adState === 'completed' && (
            embedUrl ? (
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${game.home_team_name} vs ${game.away_team_name}`}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                No video source available
              </div>
            )
          )}
        </div>
        
        {adState === 'completed' && game.video_sources?.length > 1 && (
          <div className="p-4 border-t border-sky-900/30">
            <div className="text-sm text-gray-400 mb-2">Alternative Sources:</div>
            <div className="flex flex-wrap gap-2">
              {game.video_sources.map((source, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="border-sky-400 text-sky-100 hover:bg-sky-900/50"
                  onClick={() => source.type === 'uscreen' ? window.open(source.url, '_blank') : null}
                >
                  {source.type === 'uscreen' ? 'PLUS' : source.type.toUpperCase()} {source.is_free ? '(Free)' : '(Premium)'}
                  {source.type === 'uscreen' && <ExternalLink className="w-3 h-3 ml-1" />}
                </Button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoTheater;
